import { BASE_PATH } from '@trinnvis/dabih-api-client';
import type { ConstitutionalDocumentViewModel, ContractViewModel } from '@trinnvis/dabih-api-client';
import { t } from 'src/i18n';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { key } from 'src/store/selectors/selectors';
import { type ApiClient, signingAttachment } from 'src/store/backend';
import { getClassificationText } from 'src/store/selectors';
import { sortedByName } from 'src/store/utilities';
import { getStore } from 'src/store';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import { currentUserUuid } from '../../store/selectors/user/currentUserUuid';
import { writeAccess } from '../../store/selectors/user/writeAccess';
import { toContractViewModel } from '../../store/selectors/documents/to-contract-view-model';
import { currentUserIsOwner } from '../../store/selectors/employees/current-user-is-owner';
import { employeesNotDeleted } from '../../store/selectors/employees/employees-not-deleted';
import { getOrganization } from '../../store/selectors/organization/getOrganization';
import { getOrganizationId } from '../../store/selectors/organization/getOrganizationId';
import { contactsNotDeleted } from '../../store/selectors/partners/contacts-not-deleted';
import { partnersNotDeleted } from '../../store/selectors/partners/partners-not-deleted';
import type { ContractViewEditItem } from '../../content/contracts/contract-view-edit-item';
import type { ContractView } from '../../content/contracts/contract-view';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { SelectTagOption } from '../../library/editors/elements/select-tag-option';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchRevisions } from '../fetch-revisions';
import { pagesAsSelectTagOptions } from '../pages-as-select-tag-options';
import { fetchDraft } from '../fetch-draft';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { availableEmployees } from './available-employees';

export function toSelectOptionsPartners(list): SelectTagOption[] {
  return list.map((l) => {
    return { value: l.uuid, text: l.name };
  });
}

export function toSelectOptionsEmployees(list): SelectTagOption[] {
  return list.map((l) => {
    return { value: l.uuid, text: l.firstName + ' ' + l.lastName };
  });
}

export async function buildContractView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
  entityType: 'contracts' | 'constitutionalDocuments',
): Promise<ContractView> {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const partnersForContracts = partnersNotDeleted(state)
    .filter((p) => p.isConfirmedEntity)
    .map((p) => ({
      uuid: p.uuid,
      name: p.name,
      contacts: contactsNotDeleted(state)
        .filter((c) => c.partnerUuid === p.uuid)
        .map((c) => ({
          uuid: c.uuid,
          email: c.email,
          personalIdentifier: c.personalIdentifier,
          firstName: c.firstName,
          lastName: c.lastName,
          partnerUuid: c.partnerUuid,
        })),
    }));

  let item: ContractViewModel | ConstitutionalDocumentViewModel;
  if (entityType === 'constitutionalDocuments') {
    item = organization.constitutionalDocumentsById[uuid] as ConstitutionalDocumentViewModel;
  } else {
    item = organization.contractsById[uuid] as ContractViewModel;
  }
  assertEntityFound(item, entityType, uuid);
  const entity:
    | (ContractViewModel & { contractPartyNames: string })
    | (ConstitutionalDocumentViewModel & { contractPartyNames: string }) = toContractViewModel(item, organization);

  return {
    ...commonData,
    promoData: getPromoDataForEntity(entityType, item, getFeatureStates(state)),
    docsForLinking: [],
    contentLastModified: entity.contentLastModified ?? '',
    contentLastModifiedBy: entity.contentLastModifiedBy ?? '',
    contentLastModifiedWasCreate: entity.contentLastModifiedWasCreate ?? false,
    hasTemplateDelete: entity.hasTemplateDelete ?? false,
    hasTemplateUpdate: entity.hasTemplateUpdate ?? false,
    templateDeclined: entity.templateDeclined ?? '',
    templateDeleted: entity.templateDeleted ?? '',
    templateDeletedMessage: entity.templateDeletedMessage ?? '',
    templateUpdated: entity.templateUpdated ?? '',
    currentUserHasAccess:
      entity.classification === 'NONE' || (entity.accessControl ?? []).includes(currentUserUuid(state) ?? ''),
    currentUserHasWriteAccess: writeAccess(state),
    contacts: [],
    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent: entity.helpContent ?? '',
    href: currentParent + '/' + entityType + '/' + uuid,
    pdfHref:
      BASE_PATH +
      '/organizations/' +
      organization.organizationId +
      '/' +
      entityType +
      '/' +
      uuid +
      '.pdf?key=' +
      key(state),

    parentHref: currentParent,
    deleted: entity.deleted === true,
    type: entityType,
    name: entity.name ?? '',
    classification: entity.classification ?? 'NONE',
    accessControl: entity.accessControl ?? [],
    classificationText: getClassificationText(
      entity.classification ?? 'NONE',
      entity.accessControl ?? [],
      commonData.accessControlOptions,
    ),
    content: entity.content ?? '',
    revisions: await fetchRevisions(
      api,
      entityType,
      uuid,
      organization.organizationId,
      entity.templateDeleted !== undefined && entity.templateDeleted !== '',
      undefined,
      entity.contentLastModified,
    ),
    attachments: toAttachmentItems(api, entityType, entity.uuid, entity.attachments),
    validFromDate: entity.validFromDate ?? '',
    validToDate: entity.validToDate ?? '',
    contractPartyNames: entity.contractPartyNames ?? '',
    checklist: undefined, // TODO entity.checklist === null ? undefined : entity.checklist,
    availablePages: entityType === 'contracts' ? pagesAsSelectTagOptions(state) : [],
    relationsLocked: 'relationsLocked' in entity ? (entity.relationsLocked ?? false) : false,
    activeEmployeesCount: employeesNotDeleted(state).filter((e) => {
      return e.status === 'ACTIVE';
    }).length,
    currentUserIsOwner: currentUserIsOwner(state),
    availablePartners: sortedByName(partnersForContracts),
    availableEmployees: availableEmployees(employeesNotDeleted(state), entity.employees ?? []),
    availableContacts: [],
    employees: entity.employees ?? [],
    partners: entity.partners ?? [],
    pages: ('pages' in entity && entity.pages ? (entity.pages ?? []) : []).map((x) => '' + x),
    today: LocalDate.fromString(state.today),
    deletable: true,
    signingOrders:
      entity.signingOrders?.map((s) => {
        return {
          ...s,
          asDocumentItem: async () => {
            const organizationId = getOrganizationId(getStore().getState());
            const a2 = await signingAttachment(api, entityType, entity.uuid, organizationId, s.uuid);
            return {
              name: a2.name || t('text.withoutNameLowercase'),
              url: a2.urlForInline || '',
              urlForDownload: a2.url || '',
              entityType: entityType,
              attachmentId: s.uuid,
              uuid: entity.uuid,
            };
          },
        };
      }) || [],
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, entityType, uuid, organization.organizationId);
      return command.draft as ContractViewEditItem;
    },
    organizationName: organization.name,
    unchangedTemplate: entity.unchangedTemplate ?? false,
  };
}
